/**
 * This is a (simplified) re-implementation of Blueprint3's Popover. It exists
 * to avoid pulling 1MB of BP code into the client build.
 */
import React, {useEffect, useState, useRef, useCallback} from 'react'
import { computePosition, flip, shift, offset, arrow, getScrollParents } from '@floating-ui/dom'
import type { ComputePositionReturn, Placement } from '@floating-ui/core'
import './Popover.css'

type Data = Omit<ComputePositionReturn, 'x' | 'y'> & {
	visible: boolean
	focused: boolean
	hovered: boolean
  x: number | null
  y: number | null
}

interface PopoverProps {
	hostRef: React.RefObject<Element | null>
	isOpen: boolean
	placement?: Placement
	whenFocused?: boolean
	whenHovered?: boolean
}

export default class Popover extends React.Component<PopoverProps, Data> {
	tooltipRef = null
	arrowRef = null
	scrollParents = []
	constructor(props) {
		super(props)
		this.tooltipRef = React.createRef<HTMLElement>()
		this.arrowRef = React.createRef<HTMLElement>()
		if (typeof window !== 'undefined') {
			this.state = {
				visible: !props.showEvents?.length,
				focused: props.hostRef.current === document.activeElement,
				hovered: false,
				x: null,
				y: null,
				strategy: 'absolute',
				placement: this.props.placement ?? 'bottom',
				middlewareData: { arrow: { centerOffset: 0} },
			}
		}
	}
	updatePosition = () => {
		const { hostRef, placement } = this.props
		if (this.tooltipRef?.current && hostRef.current) {
			const middleware = [offset(4), flip(), shift({padding: 4}), arrow({element: this.arrowRef.current, padding: 4})]
			computePosition(hostRef.current, this.tooltipRef.current, {
				placement,
				strategy: 'absolute',
				middleware
			}).then(pos => {
				this?.setState(pos)
			})
		}
	}
	handleShow = (inFocused, inHovered) => {
		const focused = inFocused != null ? inFocused : this.state.focused
		const hovered = inHovered != null ? inHovered : this.state.hovered
		const { whenFocused, whenHovered } = this.props
		let visible = true
		if (whenHovered && !hovered) {
			visible = false
		}
		if (whenFocused) {
			visible = focused || visible
		}
		this.setState({ visible, focused, hovered })
		this.updatePosition()
	}
	handleFocus = () => { this.handleShow(true, undefined) }
	handleBlur = () => { this.handleShow(false, undefined) }
	handleHover = () => { this.handleShow(undefined, true) }
	handleLeave = () => { this.handleShow(undefined, false) }
	componentDidMount() {
		const { hostRef, whenFocused, whenHovered } = this.props
		if (!hostRef.current?.getBoundingClientRect) {
			if (hostRef.current && !hostRef.current.getBoundingClientRect) {
				console.error('Popover configuration error: expected an element ref, got:', hostRef.current)
			}
			return
		}
		this.scrollParents = getScrollParents(hostRef.current)
		if (whenFocused) {
			hostRef.current.addEventListener('focus', this.handleFocus)
			hostRef.current.addEventListener('blur', this.handleBlur)
		}
		if (whenHovered) {
			hostRef.current.addEventListener('mouseenter', this.handleHover)
			hostRef.current.addEventListener('mouseleave', this.handleLeave)
		}
		this.scrollParents.forEach(parent => {
			parent.addEventListener('scroll', this.updatePosition)
			parent.addEventListener('resize', this.updatePosition)
		})
	}
	componentDidUpdate(prevProps) {
		if (prevProps.isOpen !== this.props.isOpen) {
			this.updatePosition()
		}
	}
	componentWillUnmount() {
		const { hostRef, whenFocused, whenHovered } = this.props
		if (whenFocused) {
			hostRef.current?.removeEventListener('focus', this.handleFocus)
			hostRef.current?.removeEventListener('blur', this.handleBlur)
		}
		if (whenHovered) {
			hostRef.current?.removeEventListener('mouseenter', this.handleHover)
			hostRef.current?.removeEventListener('mouseleave', this.handleLeave)
		}
		this.scrollParents.forEach(parent => {
			parent?.removeEventListener('scroll', this.updatePosition)
			parent?.removeEventListener('resize', this.updatePosition)
		})
	}
	render() {
		if(typeof window === 'undefined') return null
		const { isOpen, children } = this.props
		const { visible, x, y, placement: finalPlacement, middlewareData: { arrow: {x: arrowX, y: arrowY} } } = this.state

		const splitIdx = finalPlacement.indexOf('-')
		const arrowSide = {
			top: 'bottom',
			right: 'left',
			bottom: 'top',
			left: 'right',
		}[splitIdx >= 0 ? finalPlacement.slice(0, splitIdx) : finalPlacement]

		return !isOpen? null : (
			<div className={`popover-container ${ !visible ? 'popover-hidden' : ''}`} ref={this.tooltipRef} style={{
				top: y != null ? y+'px' : '',
				left: x != null ? x+'px' : '',
			}}>
				{children}
				<div className="popover-arrow" ref={this.arrowRef} style={{
					top: arrowY != null ? arrowY+'px' : '',
					left: arrowX != null ? arrowX+'px' : '',
					right: '',
					bottom: '',
					[arrowSide]: '-4px'
				}}/>
			</div>
		)
	}
}
